import { Dispatch, FC, SetStateAction, useState } from 'react';

import { RootState } from 'store/rootReducer';

import { useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaDialog, RaaModalPage, RaaSelect } from 'raa-react';

import FormObserver from 'components/fieldComponents/formObserver/FormObserver';
import HandelseForm from 'components/utkast/handelseForm/HandelseForm';
import { HandelseFormValues, StrictHandelseFormSchema } from 'components/utkast/utkastRegistration/UtkastFormSchema';

import { HandelseTyp, UpphovsmanTyp } from 'models';

import { HandelseTypEnum } from 'enums';

import { Form, Formik } from 'formik';

interface HandelseModalProps {
  handelse: HandelseFormValues;
  handelsetyper: Array<HandelseTyp>;
  onHandelseChange: (handelseFormValue: HandelseFormValues) => void;
  onSubmit: (handelseFormValue: HandelseFormValues) => void;
  onCancel?: () => void;
  onErase?: () => void;
  selectedUpphovsman: UpphovsmanTyp[];
  setSelectedUpphovsman: Dispatch<SetStateAction<UpphovsmanTyp[]>>;
}

const HandelseModal: FC<HandelseModalProps> = ({
  handelse,
  handelsetyper,
  onHandelseChange,
  onSubmit,
  onCancel,
  onErase,
  selectedUpphovsman,
  setSelectedUpphovsman
}) => {
  const { data: domaner } = useAppSelector((state: RootState) => state.bebyggelseobjektDomaner);

  const { setDialog, unsetDialog } = useDialog();

  const [localUpphovsman, setLocalUpphovsman] = useState<UpphovsmanTyp[]>(selectedUpphovsman);

  const handleSetSelectedUpphovsman = (upphovsman: UpphovsmanTyp) => {
    setSelectedUpphovsman((current) => [...current, upphovsman]);
    setLocalUpphovsman((current) => [...current, upphovsman]);
  };

  const getDefaultHandelsetyp = (type: HandelseTypEnum): HandelseTyp => {
    const handelseTyp = domaner?.handelsetyper?.find((handelseTyp: HandelseTyp) => handelseTyp.handelsetyp === type);
    if (handelseTyp) {
      return handelseTyp;
    }
    throw new Error(`Okänd händelsetyp "${type}"`);
  };

  return (
    <RaaModalPage header={handelsetyper?.length === 1 ? handelsetyper[0].namn : 'Händelse'} overflow={true}>
      {domaner && (
        <Formik
          initialValues={handelse}
          validationSchema={StrictHandelseFormSchema}
          onSubmit={(values: HandelseFormValues) => {
            onSubmit(values);
            unsetDialog();
          }}
        >
          {({ setFieldValue, values, errors, touched }) => (
            <Form className="tw-flex tw-flex-col tw-px-4 tw-pb-4 md:tw-px-6 md:tw-pb-6">
              <div className="tw-mb-2">
                {handelsetyper?.length !== 1 && (
                  <div className="tw-mx-2 tw-mb-4">
                    <label className="raa-label" htmlFor="handelsetyp">
                      Händelsetyp *
                    </label>
                    <RaaSelect
                      inputElementId="handelsetyp"
                      value={values.handelsetyp ? getDefaultHandelsetyp(values.handelsetyp) : undefined}
                      onSelect={(value) => setFieldValue('handelsetyp', value.handelsetyp, true)}
                      displayAttr="namn"
                      domain={handelsetyper ?? []}
                      placeholder="Välj ett alternativ"
                    />
                    <p className="tw-mt-1 tw-text-raa-error-red">
                      {errors.handelsetyp && touched.handelsetyp && errors.handelsetyp}
                    </p>
                  </div>
                )}
                <HandelseForm
                  selectedUpphovsman={localUpphovsman}
                  newUpphovsmanSelected={handleSetSelectedUpphovsman}
                  labelText="Färdigställt *"
                  handelseFormValue={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />
              </div>
              <FormObserver onValueChange={onHandelseChange} />

              <div className="tw-mb-2 tw-mt-6 tw-flex tw-flex-col tw-justify-between tw-px-2 md:tw-flex-row">
                <button
                  type="button"
                  className="raa-button raa-button-cancel"
                  onClick={() => {
                    onCancel && onCancel();
                    unsetDialog();
                  }}
                >
                  avbryt
                </button>
                <div className="tw-flex tw-flex-col md:tw-flex-row">
                  {onErase && (
                    <button
                      type="button"
                      className="raa-button tw-mr-0 tw-mt-2 md:tw-mr-6 md:tw-mt-0"
                      onClick={() => {
                        setDialog(RaaDialog, {
                          header: 'Vill du radera händelsen?',
                          proceed: () => {
                            onErase();
                            unsetDialog();
                            unsetDialog();
                          },
                          cancel: unsetDialog,
                          proceedButtonText: 'Radera',
                          cancelButtonText: 'Avbryt'
                        });
                      }}
                    >
                      radera
                    </button>
                  )}
                  <button
                    type="submit"
                    className="raa-button raa-button-confirm tw-mt-2 md:tw-mt-0"
                    data-testid="handelse-done"
                  >
                    Klar
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </RaaModalPage>
  );
};

export default HandelseModal;
